import { isValidPhoneNumber } from 'libphonenumber-js/core';
import metadata from 'libphonenumber-js/metadata.min.json';
import { boolean, mixed, object, string } from 'yup';

import {
  Gender,
  InsuranceType,
  Language,
  MfaAuthenticationType,
} from '../../../data/types/api/graphql';
import { isValidBirthDate } from '../../../data/utils/date.utils';
import { messages, regExEmail, trim } from '../../../data/utils/form.utils';

/**
 * A yes/no question as the form asks it. Empty is "not answered", which for
 * the record transfer question also means "not applicable".
 */
export type Answer = 'yes' | 'no' | '';

const text = (max: number) =>
  string().transform(trim).max(max, messages.maxLength.id).default('');

const requiredText = (max: number) => text(max).required(messages.required.id);

const answer = () =>
  mixed<Answer>().oneOf(['yes', 'no', ''], messages.generic.id).default('');

const requiredAnswer = () =>
  mixed<Answer>()
    .oneOf(['yes', 'no'], messages.required.id)
    .required(messages.required.id)
    .default('');

/**
 * The person as the registration form asked for it, plus the patient settings
 * the person gets on accepting. The rules follow the registration form; the
 * server checks them again and returns every field it refuses.
 */
export const personSchema = object()
  .shape({
    initials: requiredText(32),
    firstName: requiredText(255),
    lastName: requiredText(255),
    birthName: text(255),
    birthDate: string()
      .transform(trim)
      .test('max-date', messages.invalidDate.id, value =>
        isValidBirthDate(value ?? '')
      )
      .required(messages.required.id)
      .default(''),
    gender: mixed<Gender>()
      .oneOf(Object.values(Gender))
      .required(messages.required.id),
    mobileApp: boolean().default(true).nullable(),
    // Every patient with the app logs in with their own address; without the
    // app it may stay empty, such as for a child without an address.
    email: string()
      .transform(trim)
      .max(254, messages.maxLength.id)
      .email(messages.invalidEmail.id)
      .matches(regExEmail, {
        message: messages.invalidRegEx.id,
        excludeEmptyString: true,
      })
      .when('mobileApp', {
        is: true,
        then: fieldSchema => fieldSchema.required(messages.required.id),
      })
      .default(''),
    phoneNumber: string()
      .transform(trim)
      .test('international-phone', messages.invalidPhone.id, value =>
        value ? isValidPhoneNumber(value, metadata) === true : false
      )
      .required(messages.required.id)
      .default(''),
    bsn: text(16),

    // Empty means: lives at the address of the registration.
    street: text(255),
    houseNumber: text(32),
    houseNumberAddition: text(32),
    postalCode: text(16),
    city: text(255),

    minorConsent: answer(),
    pharmacyName: text(255),
    previousGpInNetherlands: requiredAnswer(),
    previousGpName: text(255).when('previousGpInNetherlands', {
      is: 'yes',
      then: fieldSchema => fieldSchema.required(messages.required.id),
    }),
    previousGpCity: text(255).when('previousGpInNetherlands', {
      is: 'yes',
      then: fieldSchema => fieldSchema.required(messages.required.id),
    }),
    previousGpNote: text(1000),
    recordTransferConsent: answer().when('previousGpInNetherlands', {
      is: 'yes',
      then: () => requiredAnswer(),
    }),
    recordTransferDeclineReason: text(1000).when('recordTransferConsent', {
      is: 'no',
      then: fieldSchema => fieldSchema.required(messages.required.id),
    }),
    insuranceType: mixed<InsuranceType>()
      .oneOf(Object.values(InsuranceType))
      .required(messages.required.id),
    insuranceNote: text(1000).when('insuranceType', {
      is: InsuranceType.Other,
      then: fieldSchema => fieldSchema.required(messages.required.id),
    }),
    insuranceCompany: text(255),
    insurancePolicyNumber: text(64),
    lspConsent: requiredAnswer(),
    contactPersonName: text(255),
    contactPersonRelation: text(255),
    contactPersonPhoneNumber: text(32),
    remarks: text(2000),

    language: mixed<Language>()
      .oneOf(Object.values(Language))
      .required(messages.required.id)
      .default(Language.Nl),
    mfaAuthenticationType: mixed<MfaAuthenticationType>()
      .oneOf(Object.values(MfaAuthenticationType))
      .required(messages.required.id)
      .default(MfaAuthenticationType.Sms),
    dutchInsurance: boolean().default(true).nullable(),
    noShowProtocol: boolean().default(false).nullable(),
  })
  .required()
  .noUnknown();
